import { useCallback } from "react";
import { omit } from "lodash";

import inputConnect from "./inputConnect";
import { getDisplayName } from "../Utils/getDisplayName";
import { useFieldContext } from "../Form/Field";
import { useInputAriaDescribedBy } from "../Form/useInputAriaDescribedBy";

function withStringFormValue(WrappedComponent) {
  const WithStringFormValue = props => {
    const { value, updateValue, onUpdate, onValueChange } = props;

    const { fieldId, isRequired, isInvalid } = useFieldContext();
    const ariaDescribedBy = useInputAriaDescribedBy();

    const updateStringValue = useCallback(
      (string, { silent } = {}) => {
        updateValue({ value: string });

        if (onValueChange && !silent) {
          onValueChange(string);
        }
      },
      [updateValue, onValueChange]
    );

    const handleChange = useCallback(
      e => updateStringValue(e.currentTarget.value),
      [updateStringValue]
    );

    // Filter out functional props here and build an object that can be spread to the
    // final input DOM element, and allow the parent component to pass it custom props.
    const filteredInputProps = omit(props, [
      "defaultValue",
      "fieldName",
      "formKey",
      "field",
      "value",
      "updateValue",
      "onUpdate",
      "onValueChange"
    ]);

    // Add contextual common props to be used by all inputs for accessibility purposes.
    const passthroughProps = {
      ...filteredInputProps,
      id: fieldId,
      "aria-required": isRequired,
      "aria-invalid": isInvalid,
      "aria-describedby": ariaDescribedBy
    };

    return (
      <WrappedComponent
        {...props}
        onChange={handleChange}
        updateStringValue={updateStringValue}
        onUpdate={onUpdate}
        value={value || ""}
        passthroughProps={passthroughProps}
      />
    );
  };

  WithStringFormValue.displayName = `WithStringFormValue(${getDisplayName(
    WrappedComponent
  )})`;

  return inputConnect(WithStringFormValue, { fields: { value: "" } });
}

export default withStringFormValue;
